import type { EnvProp, LevelDef } from './types';
import { roundedRectPath } from './geometry';

/**
 * NIVELES COMO DATOS. Nada de lógica de juego aquí.
 * Coordenadas en metros sobre el plano XZ; heading 0° mira a +Z, 90° a +X.
 */

/** Muros perimetrales del recinto de prácticas. */
function lotWalls(w: number, d: number): EnvProp[] {
  const t = 0.6;
  const h = 0.9;
  return [
    { type: 'box', pos: [0, -d / 2], size: [w, h, t], tag: 'wall', color: '#8a8f98' },
    { type: 'box', pos: [0, d / 2], size: [w, h, t], tag: 'wall', color: '#8a8f98' },
    { type: 'box', pos: [-w / 2, 0], size: [t, h, d], tag: 'wall', color: '#8a8f98' },
    { type: 'box', pos: [w / 2, 0], size: [t, h, d], tag: 'wall', color: '#8a8f98' },
  ];
}

const REGION_EU_RIGHT = { drivingSide: 'right', signSet: 'eu' } as const;
const CLEAR = { type: 'clear', gripFactor: 1 } as const;

// ---------------------------------------------------------------------------
// Nivel 1 — Arrancar y detenerse con suavidad
// ---------------------------------------------------------------------------
export const LEVEL_1: LevelDef = {
  id: 'l1-move-off',
  order: 1,
  titleKey: 'level.l1.title',
  descriptionKey: 'level.l1.desc',
  track: 'auto',
  theoryKeys: ['theory.seat', 'theory.prnd', 'theory.pedals'],
  car: 'sedan',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-42, 0], headingDeg: 90 },
  environment: {
    groundSize: [130, 100],
    props: [
      ...lotWalls(130, 100),
      { type: 'lane', points: [[-52, -2.6], [52, -2.6]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[-52, 2.6], [52, 2.6]], width: 0.15, color: 'white' },
      { type: 'zone', id: 'speedAll', kind: 'speed', pos: [0, 0], size: [130, 100], limitKmh: 40, visible: false },
    ],
  },
  objectives: [
    { id: 'prep', titleKey: 'obj.l1.prep', type: 'shift_to', gear: 'D', require: { glance: ['left', 'rear'] } },
    { id: 'go', titleKey: 'obj.l1.go', type: 'reach_speed', minKmh: 20 },
    { id: 'stop', titleKey: 'obj.l1.stop', type: 'full_stop' },
    { id: 'park', titleKey: 'obj.l1.park', type: 'shift_to', gear: 'P' },
  ],
  pass: { maxHarshAccel: 1, maxHarshBrake: 1, maxCollisions: 0, maxTimeS: 180, minOverallScore: 50 },
  unlocks: ['l2-lane'],
  xpReward: 100,
};

// ---------------------------------------------------------------------------
// Nivel 2 — Control del volante y mantenimiento del carril
// ---------------------------------------------------------------------------
const CIRCUIT = roundedRectPath(40, 28, 12, 7);

export const LEVEL_2: LevelDef = {
  id: 'l2-lane',
  order: 2,
  titleKey: 'level.l2.title',
  descriptionKey: 'level.l2.desc',
  track: 'auto',
  theoryKeys: ['theory.steering', 'theory.lane'],
  car: 'sedan',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-40, 0], headingDeg: 0 },
  environment: {
    groundSize: [130, 100],
    props: [
      ...lotWalls(130, 100),
      { type: 'lane', points: CIRCUIT, width: 3.5, color: 'white' },
      { type: 'zone', id: 'finish', kind: 'target', pos: [-40, -8], size: [7, 4] },
      { type: 'zone', id: 'speedAll', kind: 'speed', pos: [0, 0], size: [130, 100], limitKmh: 40, visible: false },
      { type: 'cone', pos: [22, 10] },
      { type: 'cone', pos: [-22, 10] },
      { type: 'cone', pos: [22, -10] },
      { type: 'cone', pos: [-22, -10] },
    ],
    paths: { circuit: { points: CIRCUIT, width: 3.5 } },
  },
  objectives: [
    { id: 'prep', titleKey: 'obj.l2.prep', type: 'shift_to', gear: 'D', require: { glance: ['left', 'rear'] } },
    { id: 'lap', titleKey: 'obj.l2.lap', type: 'follow_path', pathId: 'circuit', maxLateralM: 1.1, toZoneId: 'finish', require: { maxSpeedKmh: 40 } },
    { id: 'stop', titleKey: 'obj.l2.stop', type: 'full_stop' },
    { id: 'park', titleKey: 'obj.l2.park', type: 'shift_to', gear: 'P' },
  ],
  pass: { maxHarshAccel: 1, maxHarshBrake: 1, maxCollisions: 1, maxTimeS: 300, minOverallScore: 55, speedComplianceMin: 0.8 },
  unlocks: ['l3-stop-point'],
  xpReward: 120,
};

// ---------------------------------------------------------------------------
// Nivel 3 — Frenadas controladas y parada en punto
// ---------------------------------------------------------------------------
function stopLineProps(x: number, suffix: string): EnvProp[] {
  return [
    { type: 'zone', id: `stop${suffix}`, kind: 'stop', pos: [x, 0], size: [1.6, 5.2] },
    { type: 'lane', points: [[x, -2.6], [x, 2.6]], width: 0.4, color: 'white' },
    { type: 'sign', pos: [x + 0.3, 3.6], rotYDeg: -90, sign: 'stop' },
  ];
}

export const LEVEL_3: LevelDef = {
  id: 'l3-stop-point',
  order: 3,
  titleKey: 'level.l3.title',
  descriptionKey: 'level.l3.desc',
  track: 'auto',
  theoryKeys: ['theory.braking', 'theory.stopLine'],
  car: 'sedan',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-50, 0], headingDeg: 90 },
  environment: {
    groundSize: [130, 100],
    props: [
      ...lotWalls(130, 100),
      { type: 'lane', points: [[-56, -2.6], [56, -2.6]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[-56, 2.6], [56, 2.6]], width: 0.15, color: 'white' },
      ...stopLineProps(-10, 'A'),
      ...stopLineProps(20, 'B'),
      ...stopLineProps(45, 'C'),
      { type: 'sign', pos: [-46, 3.6], rotYDeg: -90, sign: 'speed30' },
      { type: 'zone', id: 'speedAll', kind: 'speed', pos: [0, 0], size: [130, 100], limitKmh: 40, visible: false },
    ],
  },
  objectives: [
    { id: 'prep', titleKey: 'obj.l3.prep', type: 'shift_to', gear: 'D', require: { glance: ['left', 'rear'] } },
    { id: 'go1', titleKey: 'obj.l3.go', type: 'reach_speed', minKmh: 25 },
    { id: 'stopA', titleKey: 'obj.l3.stopA', type: 'full_stop', zoneId: 'stopA' },
    { id: 'go2', titleKey: 'obj.l3.go', type: 'reach_speed', minKmh: 25 },
    { id: 'stopB', titleKey: 'obj.l3.stopB', type: 'full_stop', zoneId: 'stopB' },
    { id: 'go3', titleKey: 'obj.l3.go2', type: 'reach_speed', minKmh: 30 },
    { id: 'stopC', titleKey: 'obj.l3.stopC', type: 'full_stop', zoneId: 'stopC' },
    { id: 'park', titleKey: 'obj.l3.park', type: 'shift_to', gear: 'P' },
  ],
  pass: { maxHarshAccel: 1, maxHarshBrake: 1, maxCollisions: 0, maxTimeS: 240, minOverallScore: 55 },
  unlocks: ['l4-turns'],
  xpReward: 140,
};

// ---------------------------------------------------------------------------
// Nivel 4 — Giros con observación y señalización (intersección en T)
// ---------------------------------------------------------------------------
const TURN_R: [number, number][] = [
  [0, 1.8], [12, 1.8], [15, 1.9], [17.5, 2.6], [19.5, 4.5], [21, 7], [21.8, 10], [21.8, 34],
];
const LOOP_TOP: [number, number][] = [
  [21.8, 34], [21.8, 40], [22, 46], [20.5, 50], [17, 51], [14, 48.5], [13.6, 44], [15.5, 40.5], [18.2, 38], [18.2, 30],
];
const TURN_L: [number, number][] = [
  [18.2, 26], [18.2, 10], [18, 6], [17, 2.5], [15, 0.5], [12, -1], [8, -1.8], [-35, -1.8],
];

export const LEVEL_4: LevelDef = {
  id: 'l4-turns',
  order: 4,
  titleKey: 'level.l4.title',
  descriptionKey: 'level.l4.desc',
  track: 'auto',
  theoryKeys: ['theory.msm', 'theory.signals', 'theory.stopSign'],
  car: 'sedan',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-48, 1.8], headingDeg: 90 },
  environment: {
    groundSize: [150, 130],
    props: [
      ...lotWalls(150, 130),
      // Carretera principal (eje X)
      { type: 'lane', points: [[-70, -3.6], [70, -3.6]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[-70, 3.6], [14, 3.6]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[26, 3.6], [70, 3.6]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[-70, 0], [70, 0]], width: 0.12, color: 'yellow', dashed: true },
      // Vía lateral (eje Z, x=20)
      { type: 'lane', points: [[16.4, 3.6], [16.4, 60]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[23.6, 3.6], [23.6, 60]], width: 0.15, color: 'white' },
      { type: 'lane', points: [[20, 6], [20, 60]], width: 0.12, color: 'white', dashed: true },
      // Línea de detención
      { type: 'lane', points: [[15.4, 0], [15.4, 3.6]], width: 0.4, color: 'white' },
      { type: 'sign', pos: [15.4, 5.2], rotYDeg: -90, sign: 'stop' },
      { type: 'sign', pos: [14.8, 8.5], rotYDeg: 0, sign: 'yield' },
      { type: 'sign', pos: [-44, 5.2], rotYDeg: -90, sign: 'speed50' },
      // Zonas lógicas
      { type: 'zone', id: 'approach', kind: 'target', pos: [8, 1.8], size: [8, 3.6] },
      { type: 'zone', id: 'stopline', kind: 'stop', pos: [14.6, 1.8], size: [1.5, 3.6] },
      { type: 'zone', id: 'endA', kind: 'target', pos: [21.8, 34], size: [3.6, 6] },
      { type: 'zone', id: 'backStart', kind: 'target', pos: [18.2, 30], size: [3.6, 6], visible: false },
      { type: 'zone', id: 'yieldline', kind: 'stop', pos: [18.2, 6.5], size: [3.6, 1.6] },
      { type: 'zone', id: 'endB', kind: 'target', pos: [-42, -1.8], size: [7, 3.6] },
      { type: 'zone', id: 'speedMain', kind: 'speed', pos: [0, 0], size: [150, 8], limitKmh: 50, visible: false },
      { type: 'zone', id: 'speedSide', kind: 'speed', pos: [20, 32], size: [8, 58], limitKmh: 30, visible: false },
    ],
    paths: {
      turnR: { points: TURN_R, width: 3.6 },
      loopTop: { points: LOOP_TOP, width: 3.8 },
      turnL: { points: TURN_L, width: 3.6 },
    },
  },
  objectives: [
    { id: 'prep', titleKey: 'obj.l4.prep', type: 'shift_to', gear: 'D', require: { glance: ['left', 'rear'] } },
    { id: 'approach', titleKey: 'obj.l4.approach', type: 'drive_to_zone', zoneId: 'approach', require: { maxSpeedKmh: 40 } },
    { id: 'stop1', titleKey: 'obj.l4.stop1', type: 'full_stop', zoneId: 'stopline', require: { signal: 'right', glance: ['rear', 'right'] } },
    { id: 'turnR', titleKey: 'obj.l4.turnR', type: 'follow_path', pathId: 'turnR', maxLateralM: 1.4, toZoneId: 'endA', require: { maxSpeedKmh: 25 } },
    { id: 'loop', titleKey: 'obj.l4.loop', type: 'follow_path', pathId: 'loopTop', maxLateralM: 1.8, toZoneId: 'backStart', require: { maxSpeedKmh: 25 } },
    { id: 'stop2', titleKey: 'obj.l4.stop2', type: 'full_stop', zoneId: 'yieldline', require: { signal: 'left', glance: ['left', 'rear'] } },
    { id: 'turnL', titleKey: 'obj.l4.turnL', type: 'follow_path', pathId: 'turnL', maxLateralM: 1.4, toZoneId: 'endB', require: { maxSpeedKmh: 25, signal: 'left' } },
    { id: 'stop3', titleKey: 'obj.l4.stop3', type: 'full_stop' },
    { id: 'park', titleKey: 'obj.l4.park', type: 'shift_to', gear: 'P' },
  ],
  pass: { maxHarshAccel: 1, maxHarshBrake: 1, maxCollisions: 0, maxTimeS: 420, minObservationScore: 60, minOverallScore: 60, speedComplianceMin: 0.75 },
  unlocks: ['l5-reverse'],
  xpReward: 180,
};

// ---------------------------------------------------------------------------
// Nivel 5 — Marcha atrás
// ---------------------------------------------------------------------------
function coneRow(x0: number, x1: number, z: number, step: number): EnvProp[] {
  const props: EnvProp[] = [];
  for (let x = x0; x <= x1 + 0.01; x += step) props.push({ type: 'cone', pos: [x, z] });
  return props;
}

export const LEVEL_5: LevelDef = {
  id: 'l5-reverse',
  order: 5,
  titleKey: 'level.l5.title',
  descriptionKey: 'level.l5.desc',
  track: 'auto',
  theoryKeys: ['theory.reverse', 'theory.reverseCam'],
  car: 'sedan',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [0, 0], headingDeg: 90 },
  environment: {
    groundSize: [130, 100],
    props: [
      ...lotWalls(130, 100),
      // Pasillo de marcha atrás (detrás del coche)
      ...coneRow(-20, -2, -1.9, 3),
      ...coneRow(-20, -2, 1.9, 3),
      { type: 'zone', id: 'backA', kind: 'target', pos: [-16, 0], size: [3.5, 3.4] },
      // Plaza de aparcamiento en batería (marcha atrás)
      { type: 'lane', points: [[12.4, -2.9], [12.4, -8.3]], width: 0.12, color: 'white' },
      { type: 'lane', points: [[15.6, -2.9], [15.6, -8.3]], width: 0.12, color: 'white' },
      { type: 'lane', points: [[12.4, -8.3], [15.6, -8.3]], width: 0.12, color: 'white' },
      { type: 'cone', pos: [11.6, -8.6] },
      { type: 'cone', pos: [16.4, -8.6] },
      { type: 'cone', pos: [11.6, -2.6] },
      { type: 'cone', pos: [16.4, -2.6] },
      { type: 'zone', id: 'bay', kind: 'bay', pos: [14, -5.6], size: [3.1, 5.6] },
      { type: 'zone', id: 'preBay', kind: 'target', pos: [22, 0], size: [5, 4] },
      { type: 'zone', id: 'speedAll', kind: 'speed', pos: [0, 0], size: [130, 100], limitKmh: 30, visible: false },
    ],
  },
  objectives: [
    { id: 'prepR', titleKey: 'obj.l5.prepR', type: 'shift_to', gear: 'R', require: { glance: ['rear', 'left', 'right'] } },
    { id: 'backA', titleKey: 'obj.l5.backA', type: 'reverse_to_zone', zoneId: 'backA', require: { maxSpeedKmh: 7 } },
    { id: 'fwd', titleKey: 'obj.l5.fwd', type: 'shift_to', gear: 'D' },
    { id: 'preBay', titleKey: 'obj.l5.preBay', type: 'drive_to_zone', zoneId: 'preBay', require: { maxSpeedKmh: 15 } },
    { id: 'prepR2', titleKey: 'obj.l5.prepR2', type: 'shift_to', gear: 'R', require: { glance: ['rear', 'right'] } },
    { id: 'bay', titleKey: 'obj.l5.bay', type: 'reverse_to_zone', zoneId: 'bay', require: { maxSpeedKmh: 7 } },
    { id: 'park', titleKey: 'obj.l5.park', type: 'shift_to', gear: 'P' },
  ],
  pass: { maxHarshAccel: 1, maxHarshBrake: 1, maxCollisions: 1, maxTimeS: 420, minObservationScore: 60, minOverallScore: 60 },
  unlocks: ['free-lot', 'car:ev', 'car:city'],
  xpReward: 200,
};

// ---------------------------------------------------------------------------
// Conducción libre — sin puntuación, para ganar confianza
// ---------------------------------------------------------------------------
export const FREE_LOT: LevelDef = {
  id: 'free-lot',
  order: 99,
  titleKey: 'level.free.title',
  descriptionKey: 'level.free.desc',
  track: 'auto',
  theoryKeys: [],
  car: 'sedan',
  region: REGION_EU_RIGHT,
  weather: CLEAR,
  spawn: { pos: [-40, 0], headingDeg: 0 },
  environment: {
    groundSize: [150, 120],
    props: [
      ...lotWalls(150, 120),
      { type: 'lane', points: CIRCUIT, width: 3.5, color: 'white' },
      ...coneRow(25, 45, 15, 4),
      { type: 'lane', points: [[12.4, -22.9], [12.4, -28.3]], width: 0.12, color: 'white' },
      { type: 'lane', points: [[15.6, -22.9], [15.6, -28.3]], width: 0.12, color: 'white' },
      { type: 'lane', points: [[12.4, -28.3], [15.6, -28.3]], width: 0.12, color: 'white' },
      { type: 'ramp', pos: [55, -35], size: [12, 1.4, 30], rotYDeg: 90, pitchDeg: 7 },
    ],
    paths: { circuit: { points: CIRCUIT, width: 3.5 } },
  },
  objectives: [],
  pass: { maxHarshAccel: 99, maxHarshBrake: 99, maxCollisions: 999 },
  unlocks: [],
  xpReward: 0,
  freeRoam: true,
};

export const ALL_LEVELS: LevelDef[] = [LEVEL_1, LEVEL_2, LEVEL_3, LEVEL_4, LEVEL_5, FREE_LOT];
